import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { HomeComponent } from './home/home.component';
import { ActivitiesResolveGuard } from './resolvers/activities.resolver';
import { AppResolveGuard } from './resolvers/app.resolver';
import { BusinessesResolveGuard } from './resolvers/businesses.resolver';
import { CompaniesResolveGuard } from './resolvers/companies.resolver';
import { DashboardResolveGuard } from './resolvers/dashboard.resolver';
import { EquipmentResolveGuard } from './resolvers/equipment.resolver';
import { ErrorsResolveGuard } from './resolvers/errors.resolver';
import { EulaResolveGuard } from './resolvers/eula.resolver';
import { FieldsResolveGuard } from './resolvers/fields.resolver';
import { InspectionsResolveGuard } from './resolvers/inspections.resolver';
import { JobsResolveGuard } from './resolvers/jobs.resolver';
import { OnboardingResolveGuard } from './resolvers/onboarding.resolver';
import { PersonnelResolveGuard } from './resolvers/personnel.resolver';
import { PlansResolveGuard } from './resolvers/plans.resolver';
import { ProductsResolveGuard } from './resolvers/products.resolver';
import { SetupResolveGuard } from './resolvers/setup.resolver';
import { StockResolveGuard } from './resolvers/stock.resolver';
import { TradeTypesResolveGuard } from './resolvers/trade-types.resolver';
import { TradesResolveGuard } from './resolvers/trades.resolver';
import { JobSearchOptionsGuard } from './shared/guards/job-search-options.guard';
import { AuthGuard } from './shared/services/authorisation/authentication.guard';
import { BusinessCountGuard } from './shared/services/authorisation/business-count.guard';
import { UserPermissionGuard } from './shared/services/authorisation/user-permission.guard';

const routes: Routes = [
  {
    path: '',
    component: HomeComponent,
    canActivate: [AuthGuard],
    resolve: { resolved: AppResolveGuard },
    children: [
      {
        path: 'activities',
        loadChildren: () =>
          import('./activities/activities.module').then(
            x => x.ActivitiesModule
          ),
        resolve: { resolved: ActivitiesResolveGuard },
        canActivate: [BusinessCountGuard]
      },
      {
        path: 'businesses',
        loadChildren: () =>
          import('./businesses/businesses.module').then(
            x => x.BusinessesModule
          ),
        resolve: { resolved: BusinessesResolveGuard }
      },
      {
        path: 'companies',
        loadChildren: () =>
          import('./companies/companies.module').then(x => x.CompaniesModule),
        resolve: { resolved: CompaniesResolveGuard },
        canActivate: [BusinessCountGuard]
      },
      {
        path: 'dashboard',
        loadChildren: () =>
          import('./dashboard/dashboard.module').then(x => x.DashboardModule),
        resolve: { resolved: DashboardResolveGuard },
        canActivate: [BusinessCountGuard]
      },
      {
        path: 'equipment',
        loadChildren: () =>
          import('./equipment/equipment.module').then(x => x.EquipmentModule),
        resolve: { resolved: EquipmentResolveGuard },
        canActivate: [BusinessCountGuard]
      },
      {
        path: 'fields',
        loadChildren: () =>
          import('./fields/fields.module').then(x => x.FieldsModule),
        resolve: { resolved: FieldsResolveGuard },
        canActivate: [BusinessCountGuard]
      },
      {
        path: 'inspections',
        loadChildren: () =>
          import('./inspections/inspections.module').then(
            x => x.InspectionsModule
          ),
        resolve: { resolved: InspectionsResolveGuard },
        canActivate: [BusinessCountGuard]
      },
      {
        path: 'jobs',
        loadChildren: () =>
          import('./jobs/jobs.module').then(x => x.JobsModule),
        canDeactivate: [JobSearchOptionsGuard],
        resolve: { resolved: JobsResolveGuard },
        canActivate: [BusinessCountGuard]
      },
      {
        path: 'plans',
        loadChildren: () =>
          import('./plans/plans.module').then(x => x.PlansModule),
        canDeactivate: [JobSearchOptionsGuard],
        resolve: { resolved: PlansResolveGuard },
        canActivate: [BusinessCountGuard]
      },
      {
        path: 'personnel',
        loadChildren: () =>
          import('./personnel/personnel.module').then(x => x.PersonnelModule),
        resolve: { resolved: PersonnelResolveGuard },
        canActivate: [BusinessCountGuard]
      },
      {
        path: 'setup',
        loadChildren: () =>
          import('./setup/setup.module').then(x => x.SetupModule),
        resolve: { resolved: SetupResolveGuard }
      },
      {
        path: 'products',
        loadChildren: () =>
          import('./products/products.module').then(x => x.ProductsModule),
        resolve: { resolved: ProductsResolveGuard },
        canActivate: [BusinessCountGuard]
      },
      {
        path: 'stock',
        loadChildren: () =>
          import('./stock/stock.module').then(x => x.StockModule),
        resolve: { resolved: StockResolveGuard },
        canActivate: [BusinessCountGuard]
      },
      {
        path: 'trades',
        loadChildren: () =>
          import('./trades/trades.module').then(x => x.TradesModule),
        resolve: { resolved: TradesResolveGuard },
        canActivate: [BusinessCountGuard]
      },
      {
        path: 'tradetypes',
        loadChildren: () =>
          import('./trade-types/trade-types.module').then(
            x => x.TradeTypesModule
          ),
        resolve: { resolved: TradeTypesResolveGuard },
        canActivate: [BusinessCountGuard]
      },
      {
        path: 'recommendations',
        loadChildren: () =>
          import('./recommendations/recommendations.module').then(
            x => x.RecommendationsModule
          ),
        canActivate: [BusinessCountGuard, UserPermissionGuard]
      },
      {
        path: 'users',
        loadChildren: () =>
          import('./users/users.module').then(x => x.UsersModule),
        canActivate: [BusinessCountGuard]
      },
      {
        path: 'connections',
        loadChildren: () =>
          import('./connections/connections.module').then(
            x => x.ConnectionsModule
          ),
        canActivate: [BusinessCountGuard]
      },
      {
        path: 'settings',
        loadChildren: () =>
          import('./settings/settings.module').then(x => x.SettingsModule)
      },
      {
        path: 'nutrient-management',
        loadChildren: () =>
          import('./nutrient-management/nutrient-management.module').then(
            x => x.NutrientManagementModule
          ),
        canActivate: [BusinessCountGuard]
      }
    ]
  },
  {
    path: 'login',
    loadChildren: () => import('./login/login.module').then(x => x.LoginModule)
  },
  {
    path: 'onboarding',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./onboarding/onboarding.module').then(x => x.OnboardingModule),
    resolve: { resolved: OnboardingResolveGuard }
  },
  {
    path: 'eula',
    canActivate: [AuthGuard],
    loadChildren: () => import('./eula/eula.module').then(x => x.EulaModule),
    resolve: { resolved: EulaResolveGuard }
  },
  {
    path: 'errors',
    loadChildren: () =>
      import('./errors/errors.module').then(x => x.ErrorsModule),
    resolve: { resolved: ErrorsResolveGuard }
  },
  { path: '**', redirectTo: '' }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      enableTracing: false,
      useHash: true
    })
  ],
  exports: [RouterModule]
})
export class AppRouter {}
